import { useState, useCallback, useEffect, useRef } from 'react';

// usePanelManager — the QuickBooks-Desktop-style MDI window-manager state
// layer. See the project plan (mutable-tinkering-kernighan, TALIT PMS: MDI
// Dashboard) for the full design writeup. This file is STEP/PR 1 of that
// plan: it is a standalone, fully-working state hook that nothing imports
// yet — Dashboard.js still runs on the old single-activeTab model until a
// later PR wires this in. Safe to deploy as-is; it changes no visible
// behavior.
//
// PANEL IDENTITY — multi-instance, by user's explicit choice: a panel's id
// is `${moduleKey}:${instanceSeq}`, not just moduleKey. A user can have two
// independent "Room Management" panels open at once (e.g. working Client A
// in one, Client B in another) without either losing state. The normal nav
// click action (openOrFocusPanel) never creates a second instance on its
// own — it finds the most-recently-active existing instance of that module
// and brings it to front. A brand-new instance is only ever created
// explicitly, via openNewInstance (wired to a "+ New Window" button on each
// panel's own title bar in a later PR), or implicitly the very first time a
// module is opened (openOrFocusPanel creates one if zero exist).
//
// STATE SHAPE:
//   panels: { [panelId]: { id, moduleKey, instanceSeq, title, position,
//             size, minimized, maximized, focus } }
//   zOrder: string[]   // panel ids, back-to-front; last = frontmost/active
// A map + separate zOrder array (rather than one sorted array or per-panel
// numeric zIndex) so bring-to-front is O(n) list surgery with no z-index
// renumbering, and render z-index is just zOrder.indexOf(id).

const STORAGE_VERSION = 'v1';
const DEBOUNCE_MS = 300;
const DEFAULT_SIZE = { width: 960, height: 640 };
// New panels cascade from this origin so they don't all stack exactly on
// top of each other — matches the familiar "new window offset" convention.
const CASCADE_ORIGIN = { x: 80, y: 80 };
const CASCADE_STEP = 32;
const CASCADE_WRAP_AFTER = 8; // after this many, wrap back toward the origin

function storageKey(userId) {
  return `talit:panels:${STORAGE_VERSION}:${userId}`;
}

function clampPosition(position, size, viewport) {
  if (!viewport) return position;
  const maxX = Math.max(0, viewport.width - Math.min(size.width, viewport.width));
  const maxY = Math.max(0, viewport.height - Math.min(size.height, viewport.height));
  return {
    x: Math.min(Math.max(0, position.x), maxX),
    y: Math.min(Math.max(0, position.y), maxY),
  };
}

// Loads persisted panels for this user, validated defensively: drops any
// panel whose moduleKey no longer exists in the registry (handles a module
// being renamed/removed in a later release without crashing on old
// localStorage state), and clamps position/size back on-screen if the
// browser window is now smaller than when last saved. Deliberately does
// NOT attempt to re-validate a panel's `focus.entity` against the backend
// before rendering — every real consumer of `focus` already tolerates a
// stale/missing entity id gracefully (renders its normal list, just skips
// the scroll-to/highlight), so this reuses that existing tolerance rather
// than adding a network round-trip before the panel manager can even
// initialize.
function loadPersisted(userId, validModuleKeys, viewport) {
  if (!userId) return { panels: {}, zOrder: [] };
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return { panels: {}, zOrder: [] };
    const parsed = JSON.parse(raw);
    const panels = {};
    const zOrder = [];
    for (const id of parsed.zOrder || []) {
      const p = parsed.panels?.[id];
      if (!p || !validModuleKeys.has(p.moduleKey)) continue;
      const size = p.size || DEFAULT_SIZE;
      panels[id] = {
        ...p,
        size,
        position: clampPosition(p.position || CASCADE_ORIGIN, size, viewport),
      };
      zOrder.push(id);
    }
    return { panels, zOrder };
  } catch {
    // Malformed/corrupt localStorage — start clean rather than throw.
    return { panels: {}, zOrder: [] };
  }
}

function persist(userId, state) {
  if (!userId) return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(state));
  } catch {
    // Storage full/unavailable (private browsing, quota) — panel state
    // simply won't survive a reload; not worth surfacing to the user for
    // what is a convenience feature, not data loss (nothing here is a
    // record of a real transaction).
  }
}

// moduleRegistry: an array of { key, label, ... } — only `key`/`label` are
// read here; PanelHost (a later PR) reads the rest (component, icon, etc.).
// Passed in rather than imported so this hook has no dependency on the
// registry file's existence yet (keeps this PR's diff self-contained).
export function usePanelManager({ userId, moduleRegistry }) {
  const validModuleKeys = useRef(new Set(moduleRegistry.map((m) => m.key)));
  useEffect(() => {
    validModuleKeys.current = new Set(moduleRegistry.map((m) => m.key));
  }, [moduleRegistry]);

  const registryByKey = useRef(new Map(moduleRegistry.map((m) => [m.key, m])));
  useEffect(() => {
    registryByKey.current = new Map(moduleRegistry.map((m) => [m.key, m]));
  }, [moduleRegistry]);

  const [state, setState] = useState(() => {
    const viewport = typeof window !== 'undefined'
      ? { width: window.innerWidth, height: window.innerHeight }
      : null;
    return loadPersisted(userId, validModuleKeys.current, viewport);
  });

  // Re-load from storage if the logged-in user changes (e.g. a shared
  // kiosk machine where one login logs out and another logs in) — a
  // panel layout must never leak from one user's session into another's.
  const lastUserIdRef = useRef(userId);
  useEffect(() => {
    if (userId === lastUserIdRef.current) return;
    lastUserIdRef.current = userId;
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    setState(loadPersisted(userId, validModuleKeys.current, viewport));
  }, [userId]);

  // Debounced persistence — position/size updates during a drag/resize
  // fire on every pointermove; only the last one within DEBOUNCE_MS
  // actually hits localStorage.
  const persistTimerRef = useRef(null);
  useEffect(() => {
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      persist(userId, state);
    }, DEBOUNCE_MS);
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [userId, state]);

  const nextInstanceSeqRef = useRef({}); // moduleKey -> next seq to hand out

  const nextCascadePosition = useCallback((openCount) => {
    const step = openCount % CASCADE_WRAP_AFTER;
    return {
      x: CASCADE_ORIGIN.x + step * CASCADE_STEP,
      y: CASCADE_ORIGIN.y + step * CASCADE_STEP,
    };
  }, []);

  const createPanel = useCallback((moduleKey) => {
    const entry = registryByKey.current.get(moduleKey);
    const seq = (nextInstanceSeqRef.current[moduleKey] || 0) + 1;
    nextInstanceSeqRef.current[moduleKey] = seq;
    const id = `${moduleKey}:${seq}`;
    const title = entry?.label || moduleKey;
    return {
      id,
      moduleKey,
      instanceSeq: seq,
      title: seq > 1 ? `${title} (${seq})` : title,
      position: null, // filled in by caller via nextCascadePosition
      size: entry?.defaultSize || DEFAULT_SIZE,
      minimized: false,
      maximized: false,
      focus: null,
    };
  }, []);

  const bringToFront = useCallback((id) => {
    setState((prev) => {
      if (!prev.panels[id]) return prev;
      if (prev.zOrder[prev.zOrder.length - 1] === id) return prev; // already front
      return { ...prev, zOrder: [...prev.zOrder.filter((pid) => pid !== id), id] };
    });
  }, []);

  const restorePanel = useCallback((id) => {
    setState((prev) => {
      if (!prev.panels[id] || !prev.panels[id].minimized) return prev;
      return { ...prev, panels: { ...prev.panels, [id]: { ...prev.panels[id], minimized: false } } };
    });
  }, []);

  const minimizePanel = useCallback((id) => {
    setState((prev) => {
      if (!prev.panels[id]) return prev;
      return { ...prev, panels: { ...prev.panels, [id]: { ...prev.panels[id], minimized: true } } };
    });
  }, []);

  const toggleMaximize = useCallback((id) => {
    setState((prev) => {
      if (!prev.panels[id]) return prev;
      const p = prev.panels[id];
      return { ...prev, panels: { ...prev.panels, [id]: { ...p, maximized: !p.maximized } } };
    });
  }, []);

  const closePanel = useCallback((id) => {
    setState((prev) => {
      if (!prev.panels[id]) return prev;
      const panels = { ...prev.panels };
      delete panels[id];
      return { panels, zOrder: prev.zOrder.filter((pid) => pid !== id) };
    });
  }, []);

  const movePanel = useCallback((id, position) => {
    setState((prev) => {
      if (!prev.panels[id]) return prev;
      return { ...prev, panels: { ...prev.panels, [id]: { ...prev.panels[id], position } } };
    });
  }, []);

  const resizePanel = useCallback((id, size) => {
    setState((prev) => {
      if (!prev.panels[id]) return prev;
      return { ...prev, panels: { ...prev.panels, [id]: { ...prev.panels[id], size } } };
    });
  }, []);

  // The nav-click / notification-deep-link action: finds the
  // most-recently-active existing instance of moduleKey (nearest the front
  // of zOrder) and brings it to front + un-minimizes it. Only creates a
  // new instance if none exists yet. Never silently spawns a duplicate on
  // repeat clicks — that's the whole point of this vs. openNewInstance.
  const openOrFocusPanel = useCallback((moduleKey, { focus } = {}) => {
    let targetId = null;
    setState((prev) => {
      const existingIds = prev.zOrder.filter((id) => prev.panels[id]?.moduleKey === moduleKey);
      if (existingIds.length > 0) {
        targetId = existingIds[existingIds.length - 1]; // most-recently-active
        const panels = { ...prev.panels };
        panels[targetId] = {
          ...panels[targetId],
          minimized: false,
          focus: focus ? { entity: focus, nonce: Date.now() } : panels[targetId].focus,
        };
        return { panels, zOrder: [...prev.zOrder.filter((id) => id !== targetId), targetId] };
      }
      const panel = createPanel(moduleKey);
      targetId = panel.id;
      panel.position = nextCascadePosition(prev.zOrder.length);
      if (focus) panel.focus = { entity: focus, nonce: Date.now() };
      return {
        panels: { ...prev.panels, [panel.id]: panel },
        zOrder: [...prev.zOrder, panel.id],
      };
    });
    return targetId;
  }, [createPanel, nextCascadePosition]);

  // The explicit "+ New Window" action: always creates a brand-new,
  // independent instance regardless of how many are already open.
  const openNewInstance = useCallback((moduleKey) => {
    let newId = null;
    setState((prev) => {
      const panel = createPanel(moduleKey);
      newId = panel.id;
      panel.position = nextCascadePosition(prev.zOrder.length);
      return {
        panels: { ...prev.panels, [panel.id]: panel },
        zOrder: [...prev.zOrder, panel.id],
      };
    });
    return newId;
  }, [createPanel, nextCascadePosition]);

  // Deep-link primitive: find-or-open the panel for moduleKey (via the
  // same semantics as openOrFocusPanel — never spawns a duplicate), bring
  // it to front, un-minimize it, and arm a focus payload scoped to that
  // ONE instance. The nonce re-arms on every call (even a repeat click on
  // the same entity), matching the pre-MDI focusRequest's own convention,
  // so a consumer's effect (keyed on focus.nonce) always re-fires.
  const focusPanel = useCallback((moduleKey, entity) => {
    return openOrFocusPanel(moduleKey, { focus: entity });
  }, [openOrFocusPanel]);

  return {
    panels: state.panels,
    zOrder: state.zOrder,
    openOrFocusPanel,
    openNewInstance,
    focusPanel,
    closePanel,
    minimizePanel,
    restorePanel,
    toggleMaximize,
    bringToFront,
    movePanel,
    resizePanel,
  };
}
